import React, { type ReactElement } from 'react';
import { useFormContext } from 'react-hook-form';
import ConfirmModal, { ConfirmModalActionPair } from 'atom-components/confirm-modal';
import { countMessages } from 'pages/marking/helpers/marker.helpers';
import type { ChangeMarkerMark } from 'pages/marking/types';
import type { TestInstanceResponseMarkWithItems } from 'services/assist-api/entities/test-instance-response-mark/types';
import { type MarkResponseFormValues } from './mark-response-form.schema';

/**
 * Checks if the form should check if other marking has occurred (in order to launch the Confirm
 * Mark Creation modal). By running this check first, we prevent an unneccessary requests being
 * sent to the API if the check has already been made.
 */
export function shouldCheckIfOtherMarkingHasOccurred(
  mark: TestInstanceResponseMarkWithItems | undefined,
  formValues: MarkResponseFormValues
): boolean {
  return !mark && !formValues.otherMarkingKnown;
}

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  responseId: number;
  marksOfResponse: ChangeMarkerMark[];
};

export default function ConfirmMarkCreationModal({
  isOpen,
  onClose,
  onConfirm,
  responseId,
  marksOfResponse
}: Props): ReactElement {
  const { watch } = useFormContext();

  // By this time that this modal appears, the active marker ID should belong to the current user
  // (including for Admins), as the Confirm Marker Change modal would have appeared first for
  // marking admins if they are not already the active marker. Therefore, it is safe to assume that
  // the active marker is the current user.
  const activeMarkerId = watch('activeMarkerId');

  return (
    <ConfirmModal
      isOpen={isOpen}
      title="Confirm Mark Creation"
      handleClickCancel={onClose}
      handleClickOK={onConfirm}
      actionPair={ConfirmModalActionPair.Custom}
      buttonTitleOK="Confirm Mark Creation"
      buttonTitleCancel="Cancel"
    >
      <p>
        You are already the active marker of {countMessages(activeMarkerId, marksOfResponse)} of
        this response (<b>#{responseId}</b>).
      </p>
      <p>Are you sure you want to create this mark?</p>
    </ConfirmModal>
  );
}
